import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

function clampIndex(i, len) {
  if (len <= 0) return 0;
  return (i + len) % len;
}

function isVideoItem(item) {
  const src = item?.src || "";
  if (item?.type === "video") return true;
  return /\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(src);
}

export default function Lightbox({
  open = false,
  items = [],
  index = 0,
  onClose,
  onPrev,
  onNext,
}) {
  const len = items?.length || 0;

  const current = useMemo(() => {
    if (!len) return null;
    return items[clampIndex(index, len)];
  }, [items, index, len]);

  const videoRef = useRef(null);

  const currentIsVideo = useMemo(() => {
    return isVideoItem(current);
  }, [current]);

  useEffect(() => {
    if (!open) return;

    const html = document.documentElement;
    const body = document.body;

    // Lightbox: travar rolagem
    html.classList.add("lightbox-open");
    body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      html.classList.remove("lightbox-open");
      body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, onPrev, onNext]);

  useEffect(() => {
    if (!open) return;
    if (!currentIsVideo) return;

    const el = videoRef.current;
    if (!el) return;

    const tryPlay = () => {
      const p = el.play?.();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    tryPlay();

    const t = window.setTimeout(() => {
      const v = videoRef.current;
      if (!v) return;
      if (!v.paused) return;

      v.muted = true;
      const p2 = v.play?.();
      if (p2 && typeof p2.catch === "function") p2.catch(() => {});
    }, 60);

    return () => window.clearTimeout(t);
  }, [open, currentIsVideo, current?.src]);

  if (!open) return null;

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <button
        className="lightbox__close"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        aria-label="Fechar"
      >
        ×
      </button>

      {len > 1 ? (
        <button
          className="lightbox__nav lightbox__nav--left"
          onClick={(e) => {
            e.stopPropagation();
            onPrev?.();
          }}
          aria-label="Imagem anterior"
        >
          ‹
        </button>
      ) : null}

      <figure className="lightbox__figure" onClick={(e) => e.stopPropagation()}>
        {current?.src ? (
          currentIsVideo ? (
            <video
              className="lightbox__img"
              src={current.src}
              poster={current.poster}
              ref={videoRef}
              autoPlay
              controls
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              className="lightbox__img"
              src={current.src}
              alt={current.alt || "Imagem do portfólio Cenovix"}
            />
          )
        ) : (
          <div className="lightbox__empty">Sem imagem</div>
        )}
      </figure>

      {len > 1 ? (
        <button
          className="lightbox__nav lightbox__nav--right"
          onClick={(e) => {
            e.stopPropagation();
            onNext?.();
          }}
          aria-label="Próxima imagem"
        >
          ›
        </button>
      ) : null}
    </div>,
    document.body
  );
}
